/* eslint-disable @typescript-eslint/ban-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Row } from 'react-table';

import { DropdownInput } from '../../..';

/**
 * Configuration options that create a PublisherAndWriterFilter component based on a DropdownInput.
 * @typedef {MakePublisherAndWriterFilterInput<D>}
 * @property {DropdownInputOption} defaultOption a default option which will be listed first in
 * the DropdownInput options list
 * @property {string} elementId the HTML id to apply to the input element
 * @property {number} height optional height for DropdownInput
 * @property {number} width optional width for DropdownInput
 */
interface MakePublisherAndWriterFilterInput {
    defaultOption?: DropdownInputOption;
    elementId: string;
    height?: number;
    width?: number;
}

/**
 * A DropdownInputOption for selecting a PublisherAndWriter filter value.
 * @typedef {PublisherAndWriterOption}
 * @property {string} label a label to use for the PublisherAndWriter filter
 * @property {string} publisher the RawPerformance.Publisher
 * @property {string} writer the RawPerformance.Writer
 */
interface PublisherAndWriterOption {
    label: string;
    publisher: Maybe<string>;
    writer: Maybe<string>;
}

/**
 * Returns a React component which renders a DropdownInput that allows the user to select a single
 * option from a list of publishers and writers. See `./filterByPublishersAndWriters.ts` for details
 * on how the selected option is used to filter the rows in the table.
 * @param input {MakePublisherAndWriterFilterInput<D>}
 */

export function makePublisherAndWriterFilter<D extends PublisherAndWriterItem>({
    defaultOption,
    elementId,
    height = 45,
    width = 200,
}: MakePublisherAndWriterFilterInput): ColumnFilterComponent<D> {
    const PublisherAndWriterFilter = ({
        column,
    }: ColumnFilterProps<D>): JSX.Element => {
        const { preFilteredRows, setFilter, filterValue } = column;

        const options = useMemo((): DropdownInputOption[] => {
            const optionMap = new Map<string, PublisherAndWriterOption>();

            preFilteredRows.forEach((row: Row<D>): void => {
                const { publisher, writer } = row.original;
                let label = '';

                if (
                    (publisher?.length ?? 0) > 0 &&
                    (writer?.length ?? 0) === 0 &&
                    typeof publisher === 'string'
                ) {
                    label = publisher;
                }

                if (
                    (publisher?.length ?? 0) === 0 &&
                    (writer?.length ?? 0) > 0 &&
                    typeof writer === 'string'
                ) {
                    label = writer;
                }

                if ((publisher?.length ?? 0) > 0 && (writer?.length ?? 0) > 0) {
                    label = `${publisher} / ${writer}`;
                }

                if (label.length > 0) {
                    optionMap.set(label, {
                        label,
                        publisher,
                        writer,
                    });
                }
            });

            const optionValues = Array.from(optionMap.values());

            const finalOptions = optionValues.map(
                (
                    { label, publisher, writer }: PublisherAndWriterOption,
                    index: number,
                ): DropdownInputOption => ({
                    id: index.toString(),
                    label,
                    value: {
                        publisher,
                        writer,
                    },
                }),
            );

            if (defaultOption === undefined) {
                return finalOptions;
            }

            return [defaultOption].concat(finalOptions);
        }, [preFilteredRows]);

        const [resetTimestamp, setResetTimestamp] = useState(0);

        // If the filter value is removed, set the DropdownInput to its default value, i.e. all
        // options (no filtering)
        useEffect(() => {
            if (filterValue === undefined) {
                setResetTimestamp(Date.now());
            }
        }, [filterValue]);

        return (
            <DropdownInput
                defaultId={defaultOption?.id ?? null}
                elementId={elementId}
                handleChange={(
                    prevOption: DropdownInputOption | null,
                    option: DropdownInputOption | null,
                ): void => {
                    const publisherLookup = option?.value?.publisher ?? null;
                    const publisher =
                        typeof publisherLookup === 'string'
                            ? publisherLookup
                            : null;
                    const writerLookup = option?.value?.writer ?? null;
                    const writer =
                        typeof writerLookup === 'string' ? writerLookup : null;

                    setFilter({
                        publisher,
                        writer,
                    });
                }}
                highlightWhenOpen={false}
                options={options}
                height={height}
                width={width}
                resetTimestamp={resetTimestamp}
            />
        );
    };

    return PublisherAndWriterFilter;
}
